import { formatCurrency, formatISODate, getFormattedCurrentDate } from 'common/utils/common.util';
import { aggregateDataByDivision, noDivision } from '../CCReportCostPerDivision';
import { getComparablesLocations, renderTimePeriod } from '../construction-cost-report.util';
import { CCReport, CCReportExportData } from '../construction-cost-report.type';
import { Division } from '../../glossary/glossary.type';
import { UNDEFINED_CHANGE_ORDER_DIVISION_CODE } from '../../../common/constants/general';

export interface ExportCCReportUserInput {
  projectType: string;
  projectAddress: string;
  cdccRepresentatives: string;
  projectNumber: string;
  clientName: string;
  clientCompany: string;
  clientAddress: string;
}

export const prepareExportData = (
  report: CCReport,
  locations: string[],
  costPerDivision: any,
  divisions: Division[],
  minGSF: number,
  maxGSF: number,
  minSiteArea: number,
  maxSiteArea: number,
  minUnitCount: number,
  maxUnitCount: number,
  minBuildingCount: number,
  maxBuildingCount: number,
  minStoryCount: number,
  maxStoryCount: number,
  minBudget: number,
  maxBudget: number,
  avgBudgetPerGSF: number,
  minBudgetPerGSF: number,
  maxBudgetPerGSF: number,
  minBudgetPerNSF: number,
  maxBudgetPerNSF: number,
  avgBudgetPerNSF: number,
  minBudgetPerUnit: number,
  maxBudgetPerUnit: number,
  avgBudgetPerUnit: number,
  minPeriodEnding: Date | undefined,
  maxPeriodEnding: Date | undefined,
  includeSoftCost: boolean,
) => {
  const locationsArray = Array.from(new Set(locations.map(location => location.trim()).filter(location => location)));
  const locationsList = getComparablesLocations(report);

  const division21Id = divisions.find(d => d.code === UNDEFINED_CHANGE_ORDER_DIVISION_CODE)?.id;
  const allDivisions = [...divisions.filter(d => d.code !== UNDEFINED_CHANGE_ORDER_DIVISION_CODE), noDivision];
  const divisionData = aggregateDataByDivision(costPerDivision, allDivisions, includeSoftCost, division21Id);

  const includedCompProjects = (report.comparableProjects?.filter(comp => comp.isIncluded) ?? []).map(comp => comp.comp);
  const projectTypesArray = Array.from(
    new Set(includedCompProjects?.map(project => project.projectType).filter((type): type is string => !!type)),
  );
  const currentDateString = getFormattedCurrentDate();

  const exportData: CCReportExportData = {
    ReportName: report.name,
    ExportDate: currentDateString,
    ProjectAddress: Array.from(new Set(locations.filter(location => location))).join(' | '),
    ProjectNumber: report.projectNumber || '',
    CDCCRepresentatives: report.cdccRepresentatives || '',
    Summary: {
      projectType: projectTypesArray,
      NumberOfComparableProjects: report.comparableProjects?.filter(comp => comp.isIncluded).length || 0,
      GrossBuildingAreasRange: `${minGSF} - ${maxGSF} Sq. Ft.`,
      SiteAreasRange: `${minSiteArea} - ${maxSiteArea} Acres`,
      UnitCountsRange: `${minUnitCount} - ${maxUnitCount} Units`,
      BuildingCountsRange: `${minBuildingCount} - ${maxBuildingCount} Buildings`,
      StoryCountsRange: `${minStoryCount} - ${maxStoryCount} Stories`,
      BudgetsRange: `${formatCurrency(minBudget.toString())} - ${formatCurrency(maxBudget.toString())}`,
      DatesOfBudgetsRange: `${formatISODate(minPeriodEnding?.toString() || 'N/A', true)} - ${formatISODate(
        maxPeriodEnding?.toString() || 'N/A',
        true,
      )}`,
      Locations: locationsArray,
      LocationsList: locationsList,
      ProjectTypes: projectTypesArray,
      ProjectCompsRange: renderTimePeriod(minPeriodEnding, maxPeriodEnding).slice(2, -1) || '',
    },
    ClientData: {
      ClientName: report.clientName || '',
      ClientCompany: report.clientCompany || '',
      ClientAddress: report.clientAddress || '',
    },
    AverageCostPerGrossSF: {
      min: minBudgetPerGSF,
      max: maxBudgetPerGSF,
      avg: avgBudgetPerGSF,
    },
    AverageCostPerNetSF: {
      min: minBudgetPerNSF,
      max: maxBudgetPerNSF,
      avg: avgBudgetPerNSF,
    },
    AverageCostPerUnit: {
      min: minBudgetPerUnit,
      max: maxBudgetPerUnit,
      avg: avgBudgetPerUnit,
    },
    ProjectedCostPerGrossSF: {
      min: minBudgetPerGSF * (report?.grossBuildingSF ?? 0),
      max: maxBudgetPerGSF * (report?.grossBuildingSF ?? 0),
      avg: avgBudgetPerGSF * (report?.grossBuildingSF ?? 0),
    },
    ProjectedCostPerNetSF: {
      min: minBudgetPerNSF * (report?.netRentableSF ?? 0),
      max: maxBudgetPerNSF * (report?.netRentableSF ?? 0),
      avg: avgBudgetPerNSF * (report?.netRentableSF ?? 0),
    },
    ProjectedCostPerUnit: {
      min: minBudgetPerUnit * (report?.unitCount ?? 0),
      max: maxBudgetPerUnit * (report?.unitCount ?? 0),
      avg: avgBudgetPerUnit * (report?.unitCount ?? 0),
    },
    AverageCostPerDivision: divisionData
      .sort((a, b) => parseFloat(a.divisionCode) - parseFloat(b.divisionCode)) // Sort by divisionCode as a numeric value
      .map(division => ({
        DIVISION: `${division.divisionCode}`,
        CostPerGrossSF: {
          min: division.metrics.minBudgetPerGSF,
          max: division.metrics.maxBudgetPerGSF,
          avg: division.metrics.avgBudgetPerGSF,
        },
        CostPerUnit: {
          min: division.metrics.minBudgetPerUnit,
          max: division.metrics.maxBudgetPerUnit,
          avg: division.metrics.avgBudgetPerUnit,
        },
        CostPerNetSF: {
          min: division.metrics.minBudgetPerNSF,
          max: division.metrics.maxBudgetPerNSF,
          avg: division.metrics.avgBudgetPerNSF,
        },
        DivisionCostPercentage: division.divisionCostPercentage,
      })),
    ProjectedCostPerDivision: divisionData
      .sort((a, b) => parseFloat(a.divisionCode) - parseFloat(b.divisionCode)) // Sort by divisionCode as a numeric value
      .map(division => ({
        DIVISION: `${division.divisionCode}`,
        CostPerGrossSF: {
          min: division.metrics.minBudgetPerGSF * (report?.grossBuildingSF ?? 0),
          max: division.metrics.maxBudgetPerGSF * (report?.grossBuildingSF ?? 0),
          avg: division.metrics.avgBudgetPerGSF * (report?.grossBuildingSF ?? 0),
        },
        CostPerUnit: {
          min: division.metrics.minBudgetPerUnit * (report?.unitCount ?? 0),
          max: division.metrics.maxBudgetPerUnit * (report?.unitCount ?? 0),
          avg: division.metrics.avgBudgetPerUnit * (report?.unitCount ?? 0),
        },
        CostPerNetSF: {
          min: division.metrics.minBudgetPerNSF * (report?.netRentableSF ?? 0),
          max: division.metrics.maxBudgetPerNSF * (report?.netRentableSF ?? 0),
          avg: division.metrics.avgBudgetPerNSF * (report?.netRentableSF ?? 0),
        },
        DivisionCostPercentage: division.divisionCostPercentage,
      })),
    ProjectedCostInputs: {
      GrossBuildingSF: report?.grossBuildingSF ?? 0,
      NetRentableSF: report?.netRentableSF ?? 0,
      UnitCount: report?.unitCount ?? 0,
    },
  };

  return exportData;
};
